"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";

interface Command {
  id: string;
  name: string;
  command: string;
  description: string | null;
  working_dir: string | null;
  created_at: string;
  updated_at: string;
}

interface PanelSize {
  width: number;
  height: number;
}

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "";
const STORAGE_KEY_DESKTOP = "superone_command_panel_size_desktop";
const STORAGE_KEY_MOBILE = "superone_command_panel_size_mobile";
const STORAGE_KEY_LIST_COLLAPSED = "superone_command_list_collapsed";
const MIN_WIDTH = 500;
const MIN_HEIGHT = 400;
const MAX_WIDTH_RATIO = 0.85;
const MAX_HEIGHT_RATIO = 0.85;

export function CommandPanel({ onClose }: { onClose: () => void }) {
  const { token, user } = useAuth();
  const [commands, setCommands] = useState<Command[]>([]);
  const [selectedCommand, setSelectedCommand] = useState<Command | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // 编辑状态
  const [editName, setEditName] = useState("");
  const [editCommand, setEditCommand] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editWorkingDir, setEditWorkingDir] = useState("");

  // 执行状态
  const [executing, setExecuting] = useState(false);
  const [output, setOutput] = useState<string[]>([]);
  const [exitCode, setExitCode] = useState<number | null>(null);
  const outputRef = useRef<HTMLDivElement>(null);

  // 跟踪内容是否有变化
  const hasChanges = selectedCommand
    ? (editName !== selectedCommand.name ||
       editCommand !== selectedCommand.command ||
       editDescription !== (selectedCommand.description || "") ||
       editWorkingDir !== (selectedCommand.working_dir || ""))
    : (editName.trim() !== "" || editCommand.trim() !== "");

  // 面板大小和拖拽
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelSize, setPanelSize] = useState<PanelSize>({ width: 700, height: 500 });
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });

  // 检测是否是移动端
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth < 768;
    }
    return false;
  });

  // 检测移动端
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // 加载保存的列表折叠状态
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY_LIST_COLLAPSED);
    if (saved) {
      setListCollapsed(saved === "true");
    } else if (isMobile) {
      setListCollapsed(true);
    }
  }, [isMobile]);

  // 保存列表折叠状态
  const toggleListCollapsed = () => {
    const newState = !listCollapsed;
    setListCollapsed(newState);
    localStorage.setItem(STORAGE_KEY_LIST_COLLAPSED, String(newState));
  };

  // 加载保存的面板大小
  useEffect(() => {
    const storageKey = isMobile ? STORAGE_KEY_MOBILE : STORAGE_KEY_DESKTOP;
    const saved = localStorage.getItem(storageKey);
    if (saved && !isMobile) {
      try {
        const size = JSON.parse(saved);
        const maxWidth = window.innerWidth * MAX_WIDTH_RATIO;
        const maxHeight = window.innerHeight * MAX_HEIGHT_RATIO;
        setPanelSize({
          width: Math.min(Math.max(size.width, MIN_WIDTH), maxWidth),
          height: Math.min(Math.max(size.height, MIN_HEIGHT), maxHeight)
        });
      } catch {
        // ignore
      }
    } else if (isMobile) {
      setPanelSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    }
  }, [isMobile]);

  // 保存面板大小
  const savePanelSize = useCallback((size: PanelSize) => {
    const storageKey = isMobile ? STORAGE_KEY_MOBILE : STORAGE_KEY_DESKTOP;
    localStorage.setItem(storageKey, JSON.stringify(size));
  }, [isMobile]);

  // 拖拽调整大小
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: panelSize.width,
      height: panelSize.height
    };
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStartRef.current.x;
      const deltaY = e.clientY - resizeStartRef.current.y;

      const maxWidth = window.innerWidth * MAX_WIDTH_RATIO;
      const maxHeight = window.innerHeight * MAX_HEIGHT_RATIO;

      const newWidth = Math.min(Math.max(resizeStartRef.current.width + deltaX, MIN_WIDTH), maxWidth);
      const newHeight = Math.min(Math.max(resizeStartRef.current.height + deltaY, MIN_HEIGHT), maxHeight);

      setPanelSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      savePanelSize(panelSize);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, panelSize, savePanelSize]);

  // 自动滚动输出到底部
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  // 加载命令列表
  const loadCommands = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/commands`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCommands(data.commands || []);
      }
    } catch (error) {
      console.error("Failed to load commands:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadCommands();
  }, [loadCommands]);

  // 创建新命令
  const handleCreate = async () => {
    if (!token || !editName.trim() || !editCommand.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/commands`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: editName.trim(),
          command: editCommand.trim(),
          description: editDescription.trim() || null,
          working_dir: editWorkingDir.trim() || null
        })
      });
      if (res.ok) {
        const data = await res.json();
        setCommands([data.command, ...commands]);
        setSelectedCommand(data.command);
        setIsCreating(false);
        setIsEditing(false);
        resetEditFields(data.command);
      } else {
        const data = await res.json();
        alert(data.detail || "创建失败");
      }
    } catch (error) {
      console.error("Failed to create command:", error);
      alert("创建失败");
    } finally {
      setSaving(false);
    }
  };

  // 更新命令
  const handleUpdate = async () => {
    if (!token || !selectedCommand) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/commands/${selectedCommand.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: editName.trim(),
          command: editCommand.trim(),
          description: editDescription.trim() || null,
          working_dir: editWorkingDir.trim() || null
        })
      });
      if (res.ok) {
        const data = await res.json();
        setCommands(commands.map(c => c.id === selectedCommand.id ? data.command : c));
        setSelectedCommand(data.command);
        resetEditFields(data.command);
        setIsEditing(false);
      } else {
        const data = await res.json();
        alert(data.detail || "更新失败");
      }
    } catch (error) {
      console.error("Failed to update command:", error);
      alert("更新失败");
    } finally {
      setSaving(false);
    }
  };

  // 删除命令
  const handleDelete = async (commandId: string) => {
    if (!token) return;
    if (!confirm("确定要删除这个命令吗？")) return;

    try {
      const res = await fetch(`${API_BASE}/api/commands/${commandId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setCommands(commands.filter(c => c.id !== commandId));
        if (selectedCommand?.id === commandId) {
          setSelectedCommand(null);
          setIsEditing(false);
          clearEditFields();
        }
      } else {
        const data = await res.json();
        alert(data.detail || "删除失败");
      }
    } catch (error) {
      console.error("Failed to delete command:", error);
      alert("删除失败");
    }
  };

  // 执行命令
  const handleExecute = async () => {
    if (!token || !selectedCommand) return;

    setExecuting(true);
    setOutput([]);
    setExitCode(null);

    try {
      const res = await fetch(`${API_BASE}/api/commands/${selectedCommand.id}/execute`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ timeout: 120 })
      });

      if (!res.ok) {
        const data = await res.json();
        setOutput([`[ERROR] ${data.detail || "执行失败"}`]);
        setExecuting(false);
        return;
      }

      const reader = res.body?.getReader();
      if (!reader) {
        setOutput(["[ERROR] 无法读取响应流"]);
        setExecuting(false);
        return;
      }

      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const text = decoder.decode(value);
        const lines = text.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const content = line.substring(6);
            if (content.startsWith("[EXIT_CODE] ")) {
              const code = parseInt(content.substring(12));
              setExitCode(code);
            } else {
              setOutput(prev => [...prev, content]);
            }
          }
        }
      }
    } catch (error) {
      console.error("Failed to execute command:", error);
      setOutput(prev => [...prev, `[ERROR] ${error}`]);
    } finally {
      setExecuting(false);
    }
  };

  // 重置编辑字段
  const resetEditFields = (cmd: Command) => {
    setEditName(cmd.name);
    setEditCommand(cmd.command);
    setEditDescription(cmd.description || "");
    setEditWorkingDir(cmd.working_dir || "");
  };

  // 清空编辑字段
  const clearEditFields = () => {
    setEditName("");
    setEditCommand("");
    setEditDescription("");
    setEditWorkingDir("");
  };

  // 开始创建
  const startCreate = () => {
    setSelectedCommand(null);
    clearEditFields();
    setIsCreating(true);
    setIsEditing(true);
    setOutput([]);
    setExitCode(null);
  };

  // 取消编辑/创建
  const cancelEdit = () => {
    if (isCreating) {
      setIsCreating(false);
      clearEditFields();
      setSelectedCommand(null);
      setIsEditing(false);
    } else if (selectedCommand) {
      resetEditFields(selectedCommand);
      setIsEditing(false);
    }
  };

  // 选择命令
  const selectCommand = (cmd: Command) => {
    setSelectedCommand(cmd);
    resetEditFields(cmd);
    setIsCreating(false);
    setIsEditing(false);
    setOutput([]);
    setExitCode(null);
  };

  // 切换编辑模式
  const toggleEditMode = () => {
    setIsEditing(!isEditing);
  };

  const isAdmin = user?.role === "admin";

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/20 ${isMobile ? "z-[60]" : "z-40"}`}
        onClick={onClose}
      />
      {/* Panel */}
      <div
        ref={panelRef}
        className={`fixed shadow-2xl flex flex-col overflow-hidden select-none ${
          isMobile
            ? "inset-0 z-[61]"
            : "left-20 bottom-16 rounded-lg z-50"
        }`}
        style={isMobile ? {
          backgroundColor: "#1e1e1e"
        } : {
          backgroundColor: "#1e1e1e",
          border: "1px solid #3a3a3a",
          width: panelSize.width,
          height: panelSize.height
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#3a3a3a] flex-shrink-0">
          <div className="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="#10b981"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span className="text-[#e5e5e5] font-medium">执行命令</span>
            <span className="text-xs text-[#606060]">({commands.length})</span>
          </div>
          <div className="flex items-center gap-1">
            {/* 折叠/展开列表按钮 */}
            <button
              onClick={toggleListCollapsed}
              className="p-1.5 rounded hover:bg-[#3a3a3a] transition-colors text-[#808080] hover:text-[#e5e5e5]"
              title={listCollapsed ? "展开列表" : "折叠列表"}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 transition-transform ${listCollapsed ? "rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                />
              </svg>
            </button>
            {isAdmin && (
              <button
                onClick={startCreate}
                className="p-1.5 rounded hover:bg-[#3a3a3a] transition-colors text-[#808080] hover:text-[#e5e5e5]"
                title="新建命令"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded hover:bg-[#3a3a3a] transition-colors text-[#808080] hover:text-[#e5e5e5]"
              title="关闭"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content - 左右分栏 */}
        <div className="flex-1 flex overflow-hidden">
          {/* 左侧 - 命令列表 (可折叠) */}
          <div
            className={`flex-shrink-0 border-r border-[#2a2a2a] overflow-y-auto transition-all duration-300 ${
              listCollapsed ? "w-0 border-r-0 overflow-hidden" : "w-52"
            }`}
          >
            {loading ? (
              <div className="p-4 text-center text-[#606060] text-sm whitespace-nowrap">加载中...</div>
            ) : commands.length === 0 && !isCreating ? (
              <div className="p-4 text-center text-[#606060] text-sm whitespace-nowrap">
                <p>暂无命令</p>
                {isAdmin && (
                  <button
                    onClick={startCreate}
                    className="mt-2 text-[#10b981] hover:text-[#34d399]"
                  >
                    创建第一个
                  </button>
                )}
              </div>
            ) : (
              <div className="py-1">
                {commands.map(cmd => (
                  <div
                    key={cmd.id}
                    onClick={() => {
                      selectCommand(cmd);
                      if (isMobile) setListCollapsed(true);
                    }}
                    className={`px-3 py-2 cursor-pointer transition-colors border-l-2 whitespace-nowrap ${
                      selectedCommand?.id === cmd.id
                        ? "bg-[#2a2a2a] border-[#10b981]"
                        : "border-transparent hover:bg-[#252525]"
                    }`}
                  >
                    <div className="text-sm text-[#e5e5e5] truncate">
                      {cmd.name}
                    </div>
                    <div className="text-xs text-[#606060] truncate mt-0.5 font-mono">
                      {cmd.command.substring(0, 40)}{cmd.command.length > 40 ? "..." : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 右侧 - 详情/编辑/执行 */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {(selectedCommand || isCreating) ? (
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* 顶部工具栏 */}
                <div className="px-4 py-2 border-b border-[#2a2a2a] flex items-center justify-between flex-shrink-0">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isCreating ? (
                      <span className="text-xs text-[#10b981]">新建命令</span>
                    ) : (
                      <span className="text-xs text-[#606060] truncate">
                        {selectedCommand!.description || selectedCommand!.name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    {/* 编辑/预览切换按钮 */}
                    {!isCreating && selectedCommand && isAdmin && (
                      <button
                        onClick={toggleEditMode}
                        className={`p-1.5 rounded transition-colors ${
                          isEditing
                            ? "bg-[#10b981]/20 text-[#10b981]"
                            : "text-[#808080] hover:bg-[#3a3a3a] hover:text-[#e5e5e5]"
                        }`}
                        title={isEditing ? "预览" : "编辑"}
                      >
                        {isEditing ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        )}
                      </button>
                    )}
                    {/* 删除按钮 */}
                    {!isCreating && selectedCommand && isAdmin && (
                      <button
                        onClick={() => handleDelete(selectedCommand.id)}
                        className="p-1.5 rounded hover:bg-[#3a3a3a] transition-colors text-[#808080] hover:text-red-400"
                        title="删除"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* 内容区域 */}
                <div className="flex-1 flex flex-col p-4 overflow-hidden gap-3">
                  {isEditing || isCreating ? (
                    // 编辑模式
                    <>
                      <div>
                        <label className="block text-xs text-[#808080] mb-1">名称</label>
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          placeholder="命令名称"
                          className="w-full px-3 py-2 text-sm rounded bg-[#2a2a2a] text-[#e5e5e5] border border-[#3a3a3a] outline-none focus:border-[#10b981]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-[#808080] mb-1">命令</label>
                        <textarea
                          value={editCommand}
                          onChange={(e) => setEditCommand(e.target.value)}
                          placeholder="要执行的命令行指令"
                          rows={3}
                          className="w-full px-3 py-2 text-sm rounded bg-[#2a2a2a] text-[#e5e5e5] border border-[#3a3a3a] outline-none focus:border-[#10b981] resize-none font-mono"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-[#808080] mb-1">描述（可选）</label>
                        <input
                          type="text"
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          placeholder="命令的简短描述"
                          className="w-full px-3 py-2 text-sm rounded bg-[#2a2a2a] text-[#e5e5e5] border border-[#3a3a3a] outline-none focus:border-[#10b981]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-[#808080] mb-1">工作目录（可选）</label>
                        <input
                          type="text"
                          value={editWorkingDir}
                          onChange={(e) => setEditWorkingDir(e.target.value)}
                          placeholder="命令执行的工作目录"
                          className="w-full px-3 py-2 text-sm rounded bg-[#2a2a2a] text-[#e5e5e5] border border-[#3a3a3a] outline-none focus:border-[#10b981] font-mono"
                        />
                      </div>
                    </>
                  ) : (
                    // 预览模式 + 执行
                    <>
                      <div className="flex-shrink-0">
                        <div className="text-lg font-medium text-[#e5e5e5] mb-1">
                          {selectedCommand!.name}
                        </div>
                        {selectedCommand!.description && (
                          <div className="text-sm text-[#808080] mb-2">
                            {selectedCommand!.description}
                          </div>
                        )}
                        <div className="bg-[#2a2a2a] rounded p-3 font-mono text-sm text-[#10b981] break-all">
                          {selectedCommand!.command}
                        </div>
                        {selectedCommand!.working_dir && (
                          <div className="text-xs text-[#606060] mt-2">
                            工作目录: {selectedCommand!.working_dir}
                          </div>
                        )}
                      </div>

                      {/* 执行按钮 */}
                      {isAdmin && (
                        <div className="flex-shrink-0">
                          <button
                            onClick={handleExecute}
                            disabled={executing}
                            className="px-4 py-2 text-sm bg-[#10b981] hover:bg-[#059669] text-white rounded transition-colors disabled:opacity-50 flex items-center gap-2"
                          >
                            {executing ? (
                              <>
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                执行中...
                              </>
                            ) : (
                              <>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                执行
                              </>
                            )}
                          </button>
                        </div>
                      )}

                      {/* 输出区域 */}
                      {(output.length > 0 || executing) && (
                        <div className="flex-1 min-h-0 flex flex-col">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-[#808080]">输出</span>
                              {output.length > 0 && (
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(output.join("\n"));
                                  }}
                                  className="text-xs text-[#606060] hover:text-[#10b981] transition-colors"
                                  title="复制全部输出"
                                >
                                  复制
                                </button>
                              )}
                            </div>
                            {exitCode !== null && (
                              <span className={`text-xs ${exitCode === 0 ? "text-[#10b981]" : "text-red-400"}`}>
                                退出代码: {exitCode}
                              </span>
                            )}
                          </div>
                          <div
                            ref={outputRef}
                            className="flex-1 bg-[#0d0d0d] rounded p-3 font-mono text-xs overflow-y-auto whitespace-pre-wrap"
                            style={{ minHeight: "120px", userSelect: "text", WebkitUserSelect: "text", MozUserSelect: "text", msUserSelect: "text" }}
                          >
                            {output.map((line, i) => (
                              <div
                                key={i}
                                className={line.startsWith("[ERROR]") ? "text-red-400" : "text-[#a0a0a0]"}
                                style={{ userSelect: "text", WebkitUserSelect: "text" }}
                              >
                                {line}
                              </div>
                            ))}
                            {executing && (
                              <span className="text-[#10b981] animate-pulse">_</span>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* 底部操作栏 - 编辑模式下有变化时显示保存按钮 */}
                {(isEditing || isCreating) && (hasChanges || isCreating) && (
                  <div className="flex gap-2 px-4 pb-4 flex-shrink-0">
                    <button
                      onClick={isCreating ? handleCreate : handleUpdate}
                      disabled={saving || !editName.trim() || !editCommand.trim()}
                      className="flex-1 px-4 py-2 text-sm bg-[#10b981] hover:bg-[#059669] text-white rounded transition-colors disabled:opacity-50"
                    >
                      {saving ? "保存中..." : "保存"}
                    </button>
                    <button
                      onClick={cancelEdit}
                      disabled={saving}
                      className="px-4 py-2 text-sm bg-[#3a3a3a] hover:bg-[#4a4a4a] text-[#e5e5e5] rounded transition-colors"
                    >
                      {isCreating ? "取消" : "撤销修改"}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              // 空状态
              <div className="flex-1 flex items-center justify-center text-[#606060]">
                <div className="text-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 mx-auto mb-3 opacity-50"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-sm">选择一个命令执行</p>
                  {isAdmin && (
                    <p className="text-xs mt-1">或点击 + 创建新命令</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 拖拽调整大小的手柄 - 仅桌面端显示 */}
        {!isMobile && (
          <div
            className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
            onMouseDown={handleResizeStart}
            style={{
              background: "linear-gradient(135deg, transparent 50%, #4a4a4a 50%)"
            }}
          />
        )}
      </div>
    </>
  );
}
